import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faShoppingCart,
  faHeart,
  faShare,
  faStar,
  faMinus,
  faPlus,
  faSpinner,
  faExclamationTriangle,
  faCheck,
  faTruck,
  faShield,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import BasketSidebar from '../../common/BasketSidebar';
import { theme } from '../../../styles/theme';
import { woocommerceService } from '../../../services/woocommerceService';
import { useBasket } from '../../../contexts/BasketContext';

const ProductDetailContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 200px;
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
`;

// FAB handled globally by Navbar for shop and product detail pages

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.875rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  position: relative;
  z-index: 10;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
`;

// (Floating basket button removed; navbar controls basket)

const ProductDetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 4rem;
  margin-bottom: 4rem;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ImageSection = styled.div`
  position: relative;
  min-width: 0;
`;

const MainImage = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  background: ${props => props.theme.colors.greyBackground};
  margin-bottom: 1rem;
  position: relative;
  
  @media (max-width: 768px) {
    height: 400px;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ThumbnailImage = styled.div`
  width: 100%;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  
  &.active {
    border-color: ${props => props.theme.colors.accent};
  }
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProductInfo = styled.div`
  padding: 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  min-width: 0;
  box-sizing: border-box;
`;

const ProductTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  line-height: 1.2;
  word-break: break-word;
  hyphens: auto;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const CurrentPrice = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const OriginalPrice = styled.span`
  font-size: 1.5rem;
  color: #999;
  text-decoration: line-through;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

const Stars = styled.div`
  display: flex;
  gap: 3px;
`;

const Star = styled.span`
  color: ${props => props.filled ? '#FFD700' : '#E0E0E0'};
  font-size: 1.2rem;
`;

const RatingText = styled.span`
  font-size: 1rem;
  color: #666;
  font-weight: 500;
  margin-left: 0.5rem;
`;

const ProductDescription = styled.div`
  font-size: 1.1rem;
  color: #333;
  line-height: 1.6;
  margin-bottom: 2rem;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  
  img {
    max-width: 100%;
    height: auto;
  }
  
  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.3rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1rem;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const QuantityLabel = styled.label`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid #e0e0e0;
  border-radius: 50px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background: ${props => props.theme.colors.accent};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 60px;
  height: 40px;
  border: none;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  
  &:focus {
    outline: none;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AddToCartButton = styled.button`
  flex: 1;
  padding: 1.2rem 2rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.light};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
  
  &:disabled {
    background: ${props => props.theme.colors.greyBackground};
    color: #999;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const WishlistButton = styled.button`
  padding: 1.2rem;
  background: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
  
  &.added {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    color: white;
  }
`;

const ShareButton = styled.button`
  padding: 1.2rem;
  background: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const ProductFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 10px;
`;

const FeatureIcon = styled.div`
  color: ${props => props.theme.colors.accent};
  font-size: 1.2rem;
`;

const FeatureText = styled.span`
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6rem 2rem;
  flex-direction: column;
  gap: 1.5rem;
`;

const LoadingSpinner = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.accent};
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 6rem 2rem;
  flex-direction: column;
  gap: 1.5rem;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  color: #F44336;
`;

const ErrorText = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  max-width: 500px;
  font-weight: 500;
`;

const RetryButton = styled.button`
  padding: 1rem 2.5rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.light};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToBasket, totalItems } = useBasket();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToWishlist, setAddedToWishlist] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showBasket, setShowBasket] = useState(false);

  // Listen for global event to open basket from navbar
  useEffect(() => {
    const handleOpenBasket = () => setShowBasket(true);
    document.addEventListener('openBasketSidebar', handleOpenBasket);
    return () => document.removeEventListener('openBasketSidebar', handleOpenBasket);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        const productData = await woocommerceService.getProduct(id);
        setProduct(productData);
      } catch (err) {

        setError('Failed to load product. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    addToBasket(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleWishlist = () => {
    setAddedToWishlist(!addedToWishlist);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: product.short_description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // TODO: Show toast notification
    }
  };

  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star key={i} filled={i < fullStars}>
          ★
        </Star>
      );
    }

    return (
      <ProductRating>
        <Stars>{stars}</Stars>
        <RatingText>({rating})</RatingText>
      </ProductRating>
    );
  };

  const renderPrice = (product) => {
    if (product.sale_price && product.sale_price !== product.regular_price) {
      return (
        <ProductPrice>
          <CurrentPrice>£{product.sale_price}</CurrentPrice>
          <OriginalPrice>£{product.regular_price}</OriginalPrice>
        </ProductPrice>
      );
    }
    return (
      <ProductPrice>
        <CurrentPrice>£{product.price}</CurrentPrice>
      </ProductPrice>
    );
  };

  if (loading) {
    return (
      <ProductDetailContainer>
        <LoadingContainer>
          <LoadingSpinner>
            <FontAwesomeIcon icon={faSpinner} />
          </LoadingSpinner>
          <LoadingText>Loading product details...</LoadingText>
        </LoadingContainer>
        <Footer />
      </ProductDetailContainer>
    );
  }

  if (error) {
    return (
      <ProductDetailContainer>
        <ErrorContainer>
          <ErrorIcon>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </ErrorIcon>
          <ErrorText>{error}</ErrorText>
          <RetryButton onClick={() => window.location.reload()}>
            Try Again
          </RetryButton>
        </ErrorContainer>
        <Footer />
      </ProductDetailContainer>
    );
  }

  if (!product) {
    return (
      <ProductDetailContainer>
        <ErrorContainer>
          <ErrorIcon>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </ErrorIcon>
          <ErrorText>Product not found</ErrorText>
          <RetryButton onClick={() => navigate('/duchess-collection')}>
            Back to Shop
          </RetryButton>
        </ErrorContainer>
        <Footer />
      </ProductDetailContainer>
    );
  }

  const images = product.images || [];
  const mainImage = images[selectedImage] || images[0];

  return (
    <ProductDetailContainer>
      <ContentContainer>
        <BackButton onClick={() => navigate('/duchess-collection')}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Collection
        </BackButton>

        <ProductDetailGrid>
          <ImageSection>
            <MainImage>
              <ProductImage
                src={mainImage?.src || '/placeholder-product.jpg'}
                alt={product.name}
                onError={(e) => {
                  e.target.src = '/placeholder-product.jpg';
                }}
              />
            </MainImage>
            
            {images.length > 1 && (
              <ImageGallery>
                {images.map((image, index) => (
                  <ThumbnailImage
                    key={index}
                    className={selectedImage === index ? 'active' : ''}
                    onClick={() => setSelectedImage(index)}
                  >
                    <ThumbnailImg
                      src={image.src}
                      alt={`${product.name} ${index + 1}`}
                    />
                  </ThumbnailImage>
                ))}
              </ImageGallery>
            )}
          </ImageSection>

          <ProductInfo>
            <ProductTitle>{product.name}</ProductTitle>
            {renderPrice(product)}
            {product.average_rating > 0 && renderRating(parseFloat(product.average_rating))}
            
            <ProductDescription
              dangerouslySetInnerHTML={{ __html: product.description }}
            />

            <QuantitySelector>
              <QuantityLabel>Quantity:</QuantityLabel>
              <QuantityControls>
                <QuantityButton
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </QuantityButton>
                <QuantityInput
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 10) {
                      setQuantity(value);
                    }
                  }}
                  min="1"
                  max="10"
                />
                <QuantityButton
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= 10}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </QuantityButton>
              </QuantityControls>
            </QuantitySelector>

            <ActionButtons>
              <AddToCartButton added={addedToCart} onClick={handleAddToCart} disabled={addedToCart}>
                {addedToCart ? (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    Added to Cart!
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faShoppingCart} />
                    Add to Cart
                  </>
                )}
              </AddToCartButton>
              
              <WishlistButton
                onClick={handleWishlist}
                className={addedToWishlist ? 'added' : ''}
              >
                <FontAwesomeIcon icon={faHeart} />
              </WishlistButton>
              
              <ShareButton onClick={handleShare}>
                <FontAwesomeIcon icon={faShare} />
              </ShareButton>
            </ActionButtons>

            <ProductFeatures>
              <Feature>
                <FeatureIcon>
                  <FontAwesomeIcon icon={faTruck} />
                </FeatureIcon>
                <FeatureText>Free UK Delivery</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <FontAwesomeIcon icon={faShield} />
                </FeatureIcon>
                <FeatureText>Secure Payment</FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <FontAwesomeIcon icon={faUndo} />
                </FeatureIcon>
                <FeatureText>30 Day Returns</FeatureText>
              </Feature>
            </ProductFeatures>
          </ProductInfo>
        </ProductDetailGrid>
      </ContentContainer>
      
      <Footer />
      
      {/* Basket Sidebar */}
      <BasketSidebar 
        isOpen={showBasket} 
        onClose={() => setShowBasket(false)} 
      />
    </ProductDetailContainer>
  );
};

export default ProductDetail;